
import { Navigation, Manipulation } from "swiper";

import shopifyCdnTileResize from "~/utils/shopify-cdn-tile-resize";
import { tagableMixin } from "~/mixins";

const DIMENSION = "288";

export default {
  mixins: [tagableMixin],

  inject: { priorityCampaign: { default: { value: null } } },

  props: {
    selectedVariant: { type: Object, default: () => {} },
    product: { type: Object, required: true },
    metafields: { type: Object, default: () => {} },
    position: { type: Number, default: 0 },
    listname: { type: String, default: "" },
    images: { type: Array, default: () => [] },
    disableWishlistIcon: { type: Boolean, default: false },
    target: { type: String, default: "_self" },
  },

  data() {
    return { showNav: false, swiper: null };
  },

  computed: {
    swiperClass() {
      return `${this.product.handle}-product-title-images`;
    },
    swiperNextNavClass() {
      return `${this.product.handle}-swiper-next`;
    },
    swiperPrevNavClass() {
      return `${this.product.handle}-swiper-prev`;
    },
    swiperBegining() {
      return this.swiper?.isBegining;
    },
    swiperEnd() {
      return this.swiper?.isEnd;
    },
    swiperOptions() {
      return {
        slidesPerGroup: 1,
        slidesPerView: 1,
        loop: false,
        nested: true,
        loopFillGroupWithBlank: false,
        modules: [Navigation, Manipulation],
        navigation: {
          nextEl: `.${this.swiperNextNavClass}`,
          prevEl: `.${this.swiperPrevNavClass}`,
        },
      };
    },
    isAustralianMade() {
      return this.metafields?.australian_made == 1;
    },
    quickship() {
      return this.metafields?.quickship;
    },
    saleTagBgColor() {
      return this.priorityCampaign.value?.saleColor;
    },
    saleTagText() {
      const title = this.priorityCampaign.value?.saleTileTitle ?? "";
      return this.priorityCampaign.value?.maxDiscount
        ? title.replace(
            /__percentage__/g,
            this.priorityCampaign.value.maxDiscount
          )
        : title;
    },
    allImages() {
      return Array.isArray(this.images) ? this.images : [];
    },
    wishlistIconProps() {
      return {
        listname: this.listname,
        position: this.position,
        product: this.product,
        selectedVariant: this.selectedVariant,
      };
    },
    tags() {
      const tags =
        typeof this.product.tags === "string"
          ? this.product.tags.split(",")
          : this.product.tags;

      return tags ?? [];
    },
  },

  watch: {
    /**
     * Re-init swiper when images arrive async or change.
     */
    allImages(newVal) {
      if (!newVal?.length) return;

      this.$nextTick(() => {
        if (this.swiper && !this.swiper.destroyed) {
          try {
            this.swiper.destroy(true, true);
          } catch (e) {}
        }

        this.initSwiper();
      });
    },
  },

  async mounted() {
    if (this.allImages.length) {
      await this.initSwiper();
    }
  },

  destroyed() {
    try {
      this.swiper?.destroy?.(true, true);
    } catch (e) {}
  },

  methods: {
    async initSwiper() {
      try {
        this.swiper = await this.$swiper.init(
          `.swiper.${this.swiperClass}`,
          this.swiperOptions
        );
      } catch (e) {
        console.warn("Swiper init failed:", e);
      }
    },

    onMouseEnter() {
      if (this.allImages.length > 1) {
        this.showNav = true;
      }

      if (!this.swiper?.destroyed) {
        this.swiper?.slideNext?.();
      }
    },
    onMouseLeave() {
      this.showNav = false;
      if (!this.swiper?.destroyed) {
        this.swiper?.slidePrev?.();
      }
    },
    getOptions(image) {
      const raw = image?.src;
      const max = Number(DIMENSION);
      const resized = raw ? shopifyCdnTileResize(raw, max) : "";
      const resizedWebp = raw
        ? shopifyCdnTileResize(raw, max, { webp: true })
        : "";
      return {
        src: image?.w_288_h_288 ?? resized,
        webpSrc: image?.w_288_h_288_webp ?? resizedWebp,
        alt: this.product.title,
        widths: [DIMENSION],
        width: DIMENSION,
        height: DIMENSION,
      };
    },
  },
};
